import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/layout/Header';
import { ticketService } from '../../services/ticketService';
import { useToast } from '../../contexts/ToastContext';
import {
  Ticket,
  TicketListItem,
  TicketMessage,
  TicketStatus,
  TicketPriority,
  ticketStatusLabels,
  ticketCategoryLabels,
  allTicketStatuses,
} from '../../types/ticket';

/* -- Priority Badge -------------------------------------------- */
const priorityConfig: Record<TicketPriority, { bg: string; text: string; label: string }> = {
  [TicketPriority.Urgent]: { bg: 'bg-red-600', text: 'text-white', label: 'URGENT' },
  [TicketPriority.High]: { bg: 'bg-primary', text: 'text-white', label: 'HIGH' },
  [TicketPriority.Medium]: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'MEDIUM' },
  [TicketPriority.Low]: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'LOW' },
};

const PriorityBadge: React.FC<{ priority: TicketPriority }> = ({ priority }) => {
  const config = priorityConfig[priority];
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

/* -- Status tabs ------------------------------------------------ */
const STATUS_TABS: { key: TicketStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: TicketStatus.Open, label: 'Open' },
  { key: TicketStatus.InProgress, label: 'In Progress' },
  { key: TicketStatus.WaitingCustomer, label: 'Waiting' },
  { key: TicketStatus.Resolved, label: 'Resolved' },
  { key: TicketStatus.Closed, label: 'Closed' },
];

/* -- Time ago helper -------------------------------------------- */
const timeAgo = (dateStr: string): string => {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const formatTime = (dateStr: string): string =>
  new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatDate = (dateStr: string): string =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/* -- Ticket List Item ------------------------------------------ */
const statusDot: Record<TicketStatus, string> = {
  [TicketStatus.Open]: 'bg-blue-400',
  [TicketStatus.InProgress]: 'bg-amber-400',
  [TicketStatus.WaitingCustomer]: 'bg-orange-400',
  [TicketStatus.Resolved]: 'bg-emerald-400',
  [TicketStatus.Closed]: 'bg-slate-500',
};

const TicketListItemComponent: React.FC<{
  ticket: TicketListItem;
  isActive: boolean;
  onClick: () => void;
}> = ({ ticket, isActive, onClick }) => (
  <div
    onClick={onClick}
    className={`p-4 cursor-pointer border-b border-slate-200 transition-colors ${
      isActive
        ? 'border-l-4 border-l-primary bg-primary/5'
        : 'hover:bg-slate-50'
    }`}
  >
    <div className="flex justify-between items-start mb-1">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`h-2 w-2 rounded-full shrink-0 ${statusDot[ticket.status] ?? 'bg-slate-500'}`} />
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
          #{ticket.id.slice(0, 8)}
        </span>
      </div>
      <PriorityBadge priority={ticket.priority} />
    </div>
    <h3 className="text-sm font-semibold text-slate-900 mb-1 line-clamp-1">{ticket.subject}</h3>
    {ticket.tenantName && (
      <div className="flex items-center gap-1.5 mb-1">
        <span className="material-symbols-outlined text-xs text-primary">apartment</span>
        <span className="text-[11px] font-medium text-primary truncate">{ticket.tenantName}</span>
      </div>
    )}
    <div className="flex justify-between items-center">
      <p className="text-xs text-slate-500 truncate">
        {ticket.createdByUserName || 'Unknown'}
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-[10px] text-slate-400">{ticketStatusLabels[ticket.status]}</span>
        <span className="text-[10px] text-slate-500">{timeAgo(ticket.createdAt)}</span>
      </div>
    </div>
  </div>
);

/* -- Message Bubble -------------------------------------------- */
const MessageBubble: React.FC<{ message: TicketMessage }> = ({ message }) => {
  const isAdmin = message.isFromSupport;
  const initials = message.senderUserName
    ? message.senderUserName.split(' ').map(n => n[0]).join('').slice(0, 2)
    : '??';

  return (
    <div className={`flex gap-4 max-w-[85%] ${isAdmin ? 'ml-auto flex-row-reverse' : ''}`}>
      <div
        className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center font-bold text-sm ${
          isAdmin ? 'bg-primary text-white shadow-lg' : 'bg-slate-100 text-slate-600'
        }`}
      >
        {isAdmin ? 'ME' : initials}
      </div>
      <div className={`space-y-1 ${isAdmin ? 'text-right' : ''}`}>
        <div className={`flex items-center gap-2 ${isAdmin ? 'justify-end' : ''}`}>
          {isAdmin ? (
            <>
              <span className="text-[10px] text-slate-500">{formatTime(message.createdAt)}</span>
              <span className="text-xs font-bold text-slate-900">{message.senderUserName || 'Admin'}</span>
            </>
          ) : (
            <>
              <span className="text-xs font-bold text-slate-900">{message.senderUserName || 'Customer'}</span>
              <span className="text-[10px] text-slate-500">{formatTime(message.createdAt)}</span>
            </>
          )}
        </div>
        <div
          className={`p-4 text-sm leading-relaxed text-slate-600 whitespace-pre-line ${
            isAdmin
              ? 'bg-white rounded-xl rounded-tr-none border border-primary/40 shadow-[0_0_15px_rgba(237,12,69,0.15)] text-left'
              : 'bg-white rounded-xl rounded-tl-none border border-slate-200'
          }`}
        >
          {message.content}
        </div>
      </div>
    </div>
  );
};

/* -- Status Change Menu ---------------------------------------- */
const StatusMenu: React.FC<{
  currentStatus: TicketStatus;
  onSelect: (status: TicketStatus) => void;
  onClose: () => void;
}> = ({ currentStatus, onSelect, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div ref={menuRef} className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 py-1 w-48">
      {allTicketStatuses.map((s) => (
        <button
          key={s}
          onClick={() => { onSelect(s); onClose(); }}
          disabled={s === currentStatus}
          className={`w-full text-left px-4 py-2 text-sm transition-colors ${
            s === currentStatus
              ? 'text-primary font-semibold bg-primary/5'
              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }`}
        >
          {ticketStatusLabels[s]}
        </button>
      ))}
    </div>
  );
};

/* -- Tickets Page ---------------------------------------------- */
const TicketsPage: React.FC = () => {
  const { toast } = useToast();
  const [tickets, setTickets] = useState<TicketListItem[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [activeStatus, setActiveStatus] = useState<TicketStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load tickets
  useEffect(() => {
    const loadTickets = async () => {
      try {
        setLoading(true);
        const filter = activeStatus !== 'all' ? { status: activeStatus } : undefined;
        const res = await ticketService.getAll(filter);
        if (res.success) {
          setTickets(res.data);
        }
      } catch {
        toast.error('Failed to load tickets');
      } finally {
        setLoading(false);
      }
    };
    loadTickets();
  }, [activeStatus, toast]);

  // Load ticket detail
  const loadTicketDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      const res = await ticketService.getById(id);
      if (res.success) {
        setSelectedTicket(res.data);
        setMessages(res.data.messages || []);
      }
    } catch {
      toast.error('Failed to load ticket details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Select first ticket on list change
  useEffect(() => {
    if (tickets.length > 0 && !selectedTicket) {
      loadTicketDetail(tickets[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Filter by search query (client-side)
  const filteredTickets = tickets.filter((ticket) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.subject.toLowerCase().includes(query) ||
      ticket.createdByUserName?.toLowerCase().includes(query) ||
      ticket.id.toLowerCase().includes(query)
    );
  });

  // Send reply
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    try {
      setSendingReply(true);
      const res = await ticketService.addMessage(selectedTicket.id, {
        content: replyText.trim(),
        isInternal: false,
      });
      if (res.success) {
        setMessages((prev) => [...prev, res.data]);
        setReplyText('');
        toast.success('Reply sent');
      }
    } catch {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Change status
  const handleStatusChange = async (newStatus: TicketStatus) => {
    if (!selectedTicket) return;
    try {
      const res = await ticketService.updateStatus(selectedTicket.id, { status: newStatus });
      if (res.success) {
        setSelectedTicket((prev) => prev ? { ...prev, status: newStatus } : prev);
        setTickets((prev) =>
          prev.map((t) => (t.id === selectedTicket.id ? { ...t, status: newStatus } : t))
        );
        toast.success(`Status changed to ${ticketStatusLabels[newStatus]}`);
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <main className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50">
      <Header searchPlaceholder="Search tickets..." />

      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel: Ticket List */}
        <aside className="w-80 min-w-[320px] border-r border-slate-200 flex flex-col bg-white">
          <div className="p-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
                search
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-primary focus:border-primary text-slate-900 placeholder:text-slate-400"
                placeholder="Search tickets..."
              />
            </div>

            {/* Status Filter */}
            <div className="flex flex-wrap bg-slate-50 p-1 rounded-lg gap-0.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveStatus(tab.key);
                    setSelectedTicket(null);
                  }}
                  className={`flex-1 min-w-fit text-xs font-semibold py-1.5 px-2 rounded transition-all ${
                    activeStatus === tab.key
                      ? 'bg-primary text-white shadow-lg'
                      : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Ticket List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <span className="material-symbols-outlined text-4xl text-slate-400 mb-2 animate-spin">progress_activity</span>
                <p className="text-sm text-slate-500">Loading tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <span className="material-symbols-outlined text-4xl text-slate-400 mb-2">inbox</span>
                <p className="text-sm text-slate-500">No tickets found</p>
              </div>
            ) : (
              filteredTickets.map((ticket) => (
                <TicketListItemComponent
                  key={ticket.id}
                  ticket={ticket}
                  isActive={ticket.id === selectedTicket?.id}
                  onClick={() => loadTicketDetail(ticket.id)}
                />
              ))
            )}
          </div>
        </aside>

        {/* Center Panel: Conversation */}
        <section className="flex-1 flex flex-col">
          {selectedTicket ? (
            <>
              {/* Conversation Header */}
              <div className="h-16 border-b border-slate-200 flex items-center justify-between px-6 bg-white backdrop-blur-md">
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-900 tracking-tight truncate">
                      {selectedTicket.subject}
                    </h2>
                    <span className="text-xs font-mono text-slate-500 shrink-0">
                      #{selectedTicket.id.slice(0, 8)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {selectedTicket.createdByUserName || 'Unknown'}
                    {selectedTicket.createdByUserEmail ? ` (${selectedTicket.createdByUserEmail})` : ''}
                    {' • Started '}
                    {formatDate(selectedTicket.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="relative">
                    <button
                      onClick={() => setShowStatusMenu(!showStatusMenu)}
                      className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-900 hover:bg-slate-100 transition-all"
                    >
                      {ticketStatusLabels[selectedTicket.status]}
                      <span className="material-symbols-outlined text-sm">expand_more</span>
                    </button>
                    {showStatusMenu && (
                      <StatusMenu
                        currentStatus={selectedTicket.status}
                        onSelect={handleStatusChange}
                        onClose={() => setShowStatusMenu(false)}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Message Feed */}
              <div className="flex-1 overflow-y-auto p-6 space-y-8 bg-slate-50">
                {detailLoading ? (
                  <div className="flex justify-center py-12">
                    <span className="material-symbols-outlined text-4xl text-slate-400 animate-spin">progress_activity</span>
                  </div>
                ) : (
                  <>
                    {/* System Message */}
                    <div className="flex justify-center">
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-tighter">
                        Ticket opened • {formatDate(selectedTicket.createdAt)}
                      </span>
                    </div>

                    {/* Description as first message */}
                    {selectedTicket.description && (
                      <div className="flex gap-4 max-w-[85%]">
                        <div className="h-10 w-10 shrink-0 rounded-lg flex items-center justify-center font-bold text-sm bg-slate-100 text-slate-600">
                          {selectedTicket.createdByUserName
                            ? selectedTicket.createdByUserName.split(' ').map(n => n[0]).join('').slice(0, 2)
                            : '??'}
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-900">
                              {selectedTicket.createdByUserName || 'Customer'}
                            </span>
                            <span className="text-[10px] text-slate-500">
                              {formatTime(selectedTicket.createdAt)}
                            </span>
                          </div>
                          <div className="p-4 text-sm leading-relaxed text-slate-600 whitespace-pre-line bg-white rounded-xl rounded-tl-none border border-slate-200">
                            {selectedTicket.description}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Messages */}
                    {messages.map((message) => (
                      <MessageBubble key={message.id} message={message} />
                    ))}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t border-slate-200">
                <div className="space-y-3">
                  {/* Text Editor */}
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden focus-within:border-primary/50 transition-all">
                    {/* Toolbar */}
                    <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
                      <button className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-all">
                        <span className="material-symbols-outlined text-lg">format_bold</span>
                      </button>
                      <button className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-all">
                        <span className="material-symbols-outlined text-lg">format_italic</span>
                      </button>
                      <button className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-all">
                        <span className="material-symbols-outlined text-lg">code</span>
                      </button>
                      <button className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-all">
                        <span className="material-symbols-outlined text-lg">link</span>
                      </button>
                      <div className="w-px h-4 bg-slate-200 mx-1" />
                      <button className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-all">
                        <span className="material-symbols-outlined text-lg">attach_file</span>
                      </button>
                    </div>

                    {/* Textarea */}
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="w-full bg-transparent border-none focus:ring-0 text-sm p-4 h-32 resize-none text-slate-900 placeholder:text-slate-400"
                      placeholder="Type your reply here..."
                      disabled={sendingReply}
                      onKeyDown={(e) => {
                        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                          handleSendReply();
                        }
                      }}
                    />

                    {/* Footer */}
                    <div className="flex items-center justify-between p-3 bg-slate-50">
                      <p className="text-[10px] text-slate-500">Press Cmd + Enter to send</p>
                      <button
                        onClick={handleSendReply}
                        disabled={sendingReply || !replyText.trim()}
                        className="bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg text-sm font-bold shadow-lg shadow-primary/20 transition-all flex items-center gap-2"
                      >
                        {sendingReply ? 'Sending...' : 'Send Reply'}
                        <span className="material-symbols-outlined text-sm">send</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <span className="material-symbols-outlined text-6xl text-slate-400 mb-4">forum</span>
                <p className="text-lg font-semibold text-slate-500">Select a ticket</p>
                <p className="text-sm text-slate-400 mt-1">Choose a ticket from the list to view the conversation</p>
              </div>
            </div>
          )}
        </section>

        {/* Right Panel: Ticket Info */}
        {selectedTicket && (
          <aside className="w-72 min-w-[280px] border-l border-slate-200 p-6 overflow-y-auto bg-white">
            <div className="space-y-8">
              {/* Ticket Info */}
              <div className="text-center">
                <div className="h-20 w-20 rounded-full bg-slate-100 mx-auto mb-4 border-2 border-slate-200 flex items-center justify-center">
                  <span className="text-2xl font-bold text-slate-600">
                    {selectedTicket.createdByUserName
                      ? selectedTicket.createdByUserName.split(' ').map(n => n[0]).join('').slice(0, 2)
                      : '??'}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-slate-900">
                  {selectedTicket.createdByUserName || 'Unknown User'}
                </h3>
                {selectedTicket.createdByUserEmail && (
                  <p className="text-sm text-slate-500">{selectedTicket.createdByUserEmail}</p>
                )}
              </div>

              {/* Tenant Info */}
              {(selectedTicket.tenantName || selectedTicket.tenantId) && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Tenant / Company</h4>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                      <span className="material-symbols-outlined text-primary text-lg">apartment</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {selectedTicket.tenantName || 'Unknown Tenant'}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono truncate">
                        {selectedTicket.tenantId.slice(0, 12)}...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Ticket Details */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Ticket Details</h4>

                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Status</span>
                  <span className="text-xs font-semibold text-slate-900">{ticketStatusLabels[selectedTicket.status]}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Priority</span>
                  <PriorityBadge priority={selectedTicket.priority} />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Category</span>
                  <span className="text-xs font-semibold text-slate-900">{ticketCategoryLabels[selectedTicket.category]}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Messages</span>
                  <span className="text-xs font-semibold text-slate-900">{selectedTicket.messageCount}</span>
                </div>
                {selectedTicket.assignedToUserName && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Assigned to</span>
                    <span className="text-xs font-semibold text-slate-900">{selectedTicket.assignedToUserName}</span>
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className="space-y-4">
                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Timeline</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase">Created</p>
                    <p className="text-xs font-semibold text-slate-900">{formatDate(selectedTicket.createdAt)}</p>
                  </div>
                  {selectedTicket.updatedAt && (
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Last Updated</p>
                      <p className="text-xs font-semibold text-slate-900">{formatDate(selectedTicket.updatedAt)}</p>
                    </div>
                  )}
                  {selectedTicket.resolvedAt && (
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Resolved</p>
                      <p className="text-xs font-semibold text-slate-900">{formatDate(selectedTicket.resolvedAt)}</p>
                    </div>
                  )}
                  {selectedTicket.closedAt && (
                    <div>
                      <p className="text-[10px] text-slate-500 uppercase">Closed</p>
                      <p className="text-xs font-semibold text-slate-900">{formatDate(selectedTicket.closedAt)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </aside>
        )}
      </div>
    </main>
  );
};

export default TicketsPage;
